import Tooltip, { TooltipTrigger } from '@orn/shared-web/components/Tooltip/Tooltip';
import { cx } from '@orn/shared-web/utils/cx';
import formatElapsed from '@orn/shared-web/utils/datetime/formatElapsed';
import formatPacificDateTime from '@orn/shared-web/utils/datetime/formatPacificDateTime';
import * as React from 'react';

interface IFieldListProps {
  children: React.ReactNode;
}

/** A grid of `Field` rows, one label-column width across the scene so values line up panel to panel. */
export const FieldList = ({ children }: IFieldListProps) => (
  <dl className="grid grid-cols-[11rem_1fr] items-baseline gap-x-6 gap-y-0.5 text-sm leading-snug">{children}</dl>
);

interface IFieldProps {
  label: string;
  mono?: boolean;
  children: React.ReactNode;
}

/** A bare `<dt>`/`<dd>` label/value pair for a `FieldList`; a value too wide for its column wraps. */
export const Field = ({ label, mono = false, children }: IFieldProps) => (
  <>
    <dt className="text-fg-tertiary text-xs uppercase tracking-wider">{label}</dt>
    <dd className={cx('text-fg-primary min-w-0 break-words', mono && 'font-mono text-xs')}>{children}</dd>
  </>
);

interface IAbsentProps {
  children: React.ReactNode;
}

/** A value the record does not carry, said in words rather than left blank. */
export const Absent = ({ children }: IAbsentProps) => <span className="text-fg-tertiary italic">{children}</span>;

/** A table column's width for a `stacked` `Timestamp`: the widest stamp, `May 30, 2026, 12:59 AM PDT`, at `text-xs`. */
export const STACKED_TIMESTAMP_COLUMN_CLASS = 'w-[12rem]';

interface ITimestampProps {
  instant: string;
  /**
   * Put the elapsed half on its own line, beneath a stamp that never wraps. For
   * a table column narrow enough that the inline form wraps anyway, which breaks
   * at whatever word happens to overflow rather than between the two halves.
   * Size the column with {@link STACKED_TIMESTAMP_COLUMN_CLASS}.
   */
  stacked?: boolean;
}

/**
 * An instant in Pacific time with its distance from now alongside.
 *
 * Both halves earn their place: the absolute time is what an operator pastes
 * into a ticket or lines up against a deploy log, and the elapsed one is the
 * question they actually had. The parenthetical is dropped for an instant that
 * cannot be read, where the absolute half already surfaces the raw text.
 */
export const Timestamp = ({ instant, stacked = false }: ITimestampProps) => {
  const elapsed = formatElapsed(instant);

  if (stacked) {
    return (
      <span className="flex flex-col leading-tight">
        <span className="whitespace-nowrap">{formatPacificDateTime(instant)}</span>
        {elapsed !== null && <span className="text-fg-tertiary">({elapsed})</span>}
      </span>
    );
  }

  return (
    <>
      {formatPacificDateTime(instant)}
      {elapsed !== null && <span className="text-fg-tertiary"> ({elapsed})</span>}
    </>
  );
};

interface IHeldBadgeProps {
  /** What the hold means where this sits — an item's reading, or one message's. */
  description: string;
}

/**
 * The marker on a work item only the caregivers may read.
 *
 * Amber rather than a category hue: it is a state of the item, not another kind
 * of card, so it sits beside a category marker without reading as a second one.
 *
 * The tooltip carries the definition rather than the badge carrying a longer
 * word, because "held" also heads the Possessions table, where it means how a
 * residence or vehicle is owned. One glance cannot tell the two apart.
 */
export const HeldBadge = ({ description }: IHeldBadgeProps) => (
  <Tooltip title="Caregiver only" description={description} placement="top" wide>
    <TooltipTrigger>
      <span className="text-fg-warning-primary shrink-0 text-[10px] uppercase tracking-wide">held</span>
    </TooltipTrigger>
  </Tooltip>
);
